/**
 * Base class for all Web Components in the application.
 * Provides light DOM rendering, signal watching utilities, and lifecycle management.
 */
import { Signal } from 'signal-polyfill';

export abstract class BaseElement extends HTMLElement {
  protected cleanups: (() => void)[] = [];

  constructor() {
    super();
  }

  /**
   * Legacy accessor for components still referencing `this.shadow`.
   */
  protected get shadow(): HTMLElement {
    return this;
  }

  /**
   * Watch a signal and execute callback when it changes.
   * Cleanup is automatically handled on disconnectedCallback.
   */
  protected watch<T>(
    signal: Signal.State<T> | Signal.Computed<T>,
    callback: (value: T) => void
  ): void {
    let disposed = false;

    const watcher = new Signal.subtle.Watcher(() => {
      // Schedule effect to run after the current microtask
      queueMicrotask(() => {
        if (disposed || !this.isConnected) {
          return;
        }

        watcher.watch();
        callback(signal.get());
      });
    });

    watcher.watch(signal);
    // Run callback immediately with current value
    callback(signal.get());

    const cleanup = () => {
      disposed = true;
      const watchedSignals = Signal.subtle.introspectSources(watcher);
      if (watchedSignals.length > 0) {
        watcher.unwatch(...watchedSignals);
      }
    };

    this.cleanups.push(cleanup);
  }

  /**
   * Lifecycle callback when element is removed from DOM.
   * Cleans up all signal watchers to prevent memory leaks.
   */
  disconnectedCallback(): void {
    this.cleanups.forEach((cleanup) => cleanup());
    this.cleanups = [];
  }

  /**
   * Render HTML content to the light DOM.
   */
  protected render(html: string): void {
    this.innerHTML = html;
  }

  /**
   * Escape a string for safe insertion into HTML.
   */
  protected esc(unsafe: string): string {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Query a single element from light DOM.
   */
  protected $(selector: string): HTMLElement | null {
    return this.querySelector(selector);
  }

  /**
   * Query all matching elements from light DOM.
   */
  protected $$(selector: string): NodeListOf<HTMLElement> {
    return this.querySelectorAll(selector);
  }

  /**
   * Move all current child nodes into the target element.
   * Used by container components to restructure their authored children
   * instead of serializing via innerHTML (which destroys custom elements).
   */
  protected adoptChildrenInto(target: HTMLElement | DocumentFragment): void {
    while (this.firstChild) {
      target.appendChild(this.firstChild);
    }
  }

  /**
   * Emit a custom event from this element.
   */
  protected emit<T>(eventName: string, detail?: T): void {
    this.dispatchEvent(
      new CustomEvent(eventName, {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  }
}
